import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { browserTimeZone } from '../core/date-utils';

/** Periodo del forecast */
export type ForecastHorizon = 'month' | '3m' | '6m' | '12m' | 'custom';

export interface ForecastPeriod {
  horizon: ForecastHorizon;
  /** yyyy-MM-dd — solo para custom */
  dateFrom?: string;
  dateTo?: string;
}

export interface ForecastMonthBucket {
  /** Primer día del mes (yyyy-MM-dd). */
  month: string;
  /** Etiqueta de mes formateada en el frontend (es-MX). */
  label: string;
  collectedAmount: number;
  chargesAmount: number;
  subscriptionsAmount: number;
  totalAmount: number;
  expectedAmount: number;
  collectedCount: number;
  chargeCount: number;
  subscriptionCount: number;
}

export interface ForecastKpis {
  totalProjected: number;
  totalExpected: number;
  chargesProjected: number;
  subscriptionsProjected: number;
  mrr: number;
  atRiskAmount: number;
  overdueAmount: number;
  collectedInHorizon: number;
  activeSubscriptions: number;
  eventCount: number;
}

export interface ForecastCashFlowData {
  from: string;
  to: string;
  kpis: ForecastKpis;
  months: ForecastMonthBucket[];
  byFrequency: { frequency: string; amount: number; count: number }[];
  byCollectionMethod: { method: string; amount: number; count: number }[];
  loading: boolean;
  error?: string;
}

interface CashFlowMonthApi {
  month?: string;
  collectedAmount?: number;
  chargesAmount?: number;
  subscriptionsAmount?: number;
  totalAmount?: number;
  expectedAmount?: number;
  collectedCount?: number;
  chargeCount?: number;
  subscriptionCount?: number;
}

interface CashFlowForecastApi {
  from?: string;
  to?: string;
  kpis?: Partial<ForecastKpis>;
  months?: CashFlowMonthApi[];
  byFrequency?: { key?: string; amount?: number; count?: number }[];
  byCollectionMethod?: { key?: string; amount?: number; count?: number }[];
}

interface WrapperCashFlowForecast {
  ok?: boolean;
  message?: string;
  body?: CashFlowForecastApi | null;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Interpreta yyyy-MM-dd como fecha local (sin UTC). */
function parseLocalDate(iso: string | undefined): Date | null {
  if (!iso || iso.length < 10) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatMonthLabel(iso: string | undefined): string {
  const date = parseLocalDate(iso);
  if (!date) return iso || '';
  return date.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

export function getForecastRange(period: ForecastPeriod): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  if (period.horizon === 'custom' && period.dateFrom && period.dateTo) {
    return {
      from: startOfDay(new Date(period.dateFrom + 'T00:00:00')),
      to: endOfDay(new Date(period.dateTo + 'T00:00:00'))
    };
  }
  const monthStart = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
  if (period.horizon === 'month') {
    const to = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    return { from: monthStart, to };
  }
  const months = period.horizon === '3m' ? 3 : period.horizon === '6m' ? 6 : 12;
  const to = endOfDay(new Date(today.getFullYear(), today.getMonth() + months, 0));
  return { from: monthStart, to };
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

@Injectable({ providedIn: 'root' })
export class ForecastCashFlowService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  loadForecast(period: ForecastPeriod = { horizon: '6m' }): Observable<ForecastCashFlowData> {
    const { from, to } = getForecastRange(period);
    const params = new HttpParams()
      .set('from', toDayKey(from))
      .set('to', toDayKey(to))
      .set('timeZone', browserTimeZone());

    return this.http
      .get<WrapperCashFlowForecast>(`${this.apiUrl}/forecasts/cash-flow`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.emptyForecast(from, to, res?.message || 'No se pudo cargar el forecast');
          }
          return this.mapApi(res.body);
        }),
        catchError((err) =>
          of(this.emptyForecast(from, to, err?.error?.message || err?.message || 'Error al cargar el forecast'))
        )
      );
  }

  private mapApi(body: CashFlowForecastApi): ForecastCashFlowData {
    const k = body.kpis ?? {};
    return {
      from: body.from ?? '',
      to: body.to ?? '',
      kpis: {
        totalProjected: num(k.totalProjected),
        totalExpected: num(k.totalExpected),
        chargesProjected: num(k.chargesProjected),
        subscriptionsProjected: num(k.subscriptionsProjected),
        mrr: num(k.mrr),
        atRiskAmount: num(k.atRiskAmount),
        overdueAmount: num(k.overdueAmount),
        collectedInHorizon: num(k.collectedInHorizon),
        activeSubscriptions: num(k.activeSubscriptions),
        eventCount: num(k.eventCount)
      },
      months: (body.months ?? []).map((m) => ({
        month: m.month ?? '',
        label: formatMonthLabel(m.month),
        collectedAmount: num(m.collectedAmount),
        chargesAmount: num(m.chargesAmount),
        subscriptionsAmount: num(m.subscriptionsAmount),
        totalAmount: num(m.totalAmount),
        expectedAmount: num(m.expectedAmount),
        collectedCount: num(m.collectedCount),
        chargeCount: num(m.chargeCount),
        subscriptionCount: num(m.subscriptionCount)
      })),
      byFrequency: (body.byFrequency ?? []).map((r) => ({
        frequency: r.key ?? '—',
        amount: num(r.amount),
        count: num(r.count)
      })),
      byCollectionMethod: (body.byCollectionMethod ?? []).map((r) => ({
        method: r.key ?? '—',
        amount: num(r.amount),
        count: num(r.count)
      })),
      loading: false
    };
  }

  private emptyForecast(from: Date, to: Date, error?: string): ForecastCashFlowData {
    return {
      from: toDayKey(from),
      to: toDayKey(to),
      kpis: {
        totalProjected: 0,
        totalExpected: 0,
        chargesProjected: 0,
        subscriptionsProjected: 0,
        mrr: 0,
        atRiskAmount: 0,
        overdueAmount: 0,
        collectedInHorizon: 0,
        activeSubscriptions: 0,
        eventCount: 0
      },
      months: [],
      byFrequency: [],
      byCollectionMethod: [],
      loading: false,
      error
    };
  }
}
